import React, { useState, useEffect } from "react";
import {
  Breadcrumb,
  Divider,
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Row,
  Col,
  Space,
  Badge,
  Popconfirm,
  Empty,
  Select,
  Tabs,
  Tag,
} from "antd";
import * as Icon from "react-feather";
import { useDispatch } from "react-redux";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import CustomTable from "../../components/table/custom-table";
import { useCreatedSort, withCreatedColumn } from '../../shared/functional/created-sort';
import AddFooterLinkDialog from "./AddEditFooterLinkDialog";
import fileUtils from "../../utils/file-utils";
import { groupType, PREVIEW_TYPE } from "../../constants/app-constants";
import footerLinkService from "../../services/footer-link-service";
import footerSettingsService from "../../services/footer-settings-service";
import serviceLinkService from "../../services/service-link-service";
import arrayMove from "array-move";
import SortableList from "../../components/sort/sortable-list";
import userService from "../../services/user-service";
import { openWebsitePreview } from "../../utils/preview-utils";

const { Panel } = Collapse;
const { Option } = Select;
const { TabPane } = Tabs;

/**
 * NMU-421: the footer is managed as three equal layers.
 *
 * Each layer gets its own tab with the same controls — card grid preview, add
 * button and data table — its own saved arrangement, and its own cap. Layer C
 * used to be a collapsible contact-info form; it is a link layer like A and B
 * now, and the contact fields moved to their own section below the tabs.
 */
const FOOTER_LAYERS = ["A", "B", "C"];
const MAX_LINKS_PER_LAYER = 4;

function callback(key) {
  console.log(key);
}

const FooterLink = () => {
  const dispatch = useDispatch();
  const [isShowAddFileDialog, setIsShowAddFileDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [condition, setCondition] = useState({ title: null, status: null });
  const [dataList, setDataList] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    rowPerPage: 10,
    rowCount: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [displayByLayer, setDisplayByLayer] = useState({ A: [], B: [], C: [] });
  const [activeLayer, setActiveLayer] = useState("A");
  const [userList, setUserList] = useState([]);

  // The arrangement of the tab currently on screen.
  const displayList = displayByLayer[activeLayer] || [];
  const isLayerFull = displayList.length >= MAX_LINKS_PER_LAYER;

  const setDisplayForLayer = (layer, list) =>
    setDisplayByLayer((prev) => ({ ...prev, [layer]: list }));

  // Contact details shown at the very bottom of the public footer. A single
  // settings document, not a list, so it is edited inline rather than through
  // the layer tabs. It used to be labelled "Layer C" — that name now belongs to
  // the third link layer.
  const emptyFooterSettings = {
    addressTH: "",
    addressEN: "",
    phone: "",
    email: "",
    mapsUrl: "",
    universityOfficesUrl: "",
    facebookUrl: "",
    twitterUrl: "",
  };
  const [footerSettings, setFooterSettings] = useState(emptyFooterSettings);

  /**
   * NMU-450: which layers a link is currently arranged into. Read straight off
   * the arrangements already loaded for the tabs, so the shared pool shows at a
   * glance where each link is used — and that one link can be used in several
   * layers at once, which is the point of making the pool common.
   */
  const layersUsingLink = (footerLinkId) =>
    FOOTER_LAYERS.filter((layer) =>
      (displayByLayer[layer] || []).some((item) => item.footerLinkId === footerLinkId)
    );

  /*
    NMU-580: the วันที่/เวลาสร้าง column and the sort behind it.

    The server does the ordering — this table shows one page of many — so a
    click re-runs the search in the new order, back on page 1: the newest
    record is not on page 3 of the same list read the other way round.
  */
  const { sort, createdColumn, onChangeTable } = useCreatedSort((next) =>
    doSearch(1, { condition, sort: next }));

  const columns = [
    {
      title: "ชื่อลิงค์ส่วนท้าย",
      dataIndex: "title",
      key: "name",
      render: (record) => {
        return (
          <a>{record?.titleTH != null ? record?.titleTH : record?.titleEN}</a>
        );
      },
    },
    {
      title: "ลิงค์",
      dataIndex: "url",
      key: "url",
      render: (data) => {
        return (
          <a
            href="#"
            onClick={() => {
              window.open(
                data,
                "winname",
                "directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no"
              );
            }}
          >
            ลิงค์
          </a>
        );
      },
    },
    {
      /* NMU-450: the pool is shared, so show where each link is actually placed. */
      title: "ใช้ในเลเยอร์",
      key: "layers",
      align: "center",
      width: 150,
      render: (record) => {
        const layers = layersUsingLink(record.footerLinkId);
        if (!layers.length) {
          return <span style={{ color: "#bfbfbf" }}>ยังไม่ได้ใช้</span>;
        }
        return layers.map((layer) => (
          <Tag color="green" key={layer}>{`Layer ${layer}`}</Tag>
        ));
      },
    },
    {
      title: "จำนวนผู้เข้าชม",
      dataIndex: "totalView",
      key: "totalView",
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Badge
          color={text ? "#4A9A0F" : "#CBCACA"}
          className="text-nowrap"
          text={text ? "Online" : "Offline"}
        />
      ),
    },
    {
      title: "ผู้บันทึก",
      key: "createBy",
      dataIndex: "createBy",
      render: (createBy) => {
        let check = userList.find((user) => createBy === user.userId);
        if (check) {
          return `${check.profile?.firstName} ${check.profile?.lastName}`;
        } else {
          return `undefined`;
        }
      },
    },
    {
      title: "ผู้แก้ไข",
      dataIndex: "updateBy",
      key: "updateBy",
      render: (updateBy) => {
        let check = userList.find((user) => updateBy === user.userId);
        if (check) {
          return `${check.profile?.firstName} ${check.profile?.lastName}`;
        } else {
          return `undefined`;
        }
      },
    },

    {
      title: "ดำเนินการ",
      key: "action",
      fixed: "right",
      width: 100,
      render: (record) => (
        <Space size="middle">
          <a onClick={() => onClickEdit(record)} className="pe-3 border-end">
            <Icon.Edit size={18} />
          </a>

          <Popconfirm
            title="ยืนยันการลบข้อมูล ?"
            onConfirm={() => onConfirmDelete(record.footerLinkId)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
          >
            <a>
              <Icon.Trash size={18} />
            </a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    async function initData() {
      const response = await userService.search();
      setUserList(response.list);
    }
    initData();
    doSearch(1);
    doSearchAllDisplays();
    doLoadFooterSettings();
  }, []);

  // Switching tabs reloads that layer's table; the arrangements are already in
  // hand from mount, so only the paged list needs re-fetching.
  useEffect(() => {
    doSearch(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayer]);

  useEffect(() => {
    if (editData) {
      onEdit(editData);
    }
  }, [editData]);

  const onAdd = () => {
    setEditData(null);
    setIsShowAddFileDialog(true);
  };

  const onCancel = () => {
    setIsShowAddFileDialog(false);
    setEditData(null);
  };

  const onSaveComplete = () => {
    setEditData(null);
    setIsShowAddFileDialog(false);
    doSearch(1);
  };

  const onEdit = (data) => {
    setEditData(data);
    setIsShowAddFileDialog(true);
  };

  const onChangeCondition = (event) => {
    const { name, value } = event.target;
    setCondition({ ...condition, [name]: value });
  };

  const onChangePage = (currentPage) => {
    doSearch(currentPage);
  };

  /**
   * @param currentPage page to land on, 1 to start over.
   * @param overrides   NMU-529: search with these filters instead of the ones in
   *   state. setCondition only takes effect on the next render, so a caller that
   *   resets the filters and searches in the same handler would otherwise re-run
   *   the search it just cleared.
   */
  const doSearch = async (currentPage, overrides) => {
    try {
      const activeSort = overrides && overrides.sort ? overrides.sort : sort;
      const newPaginate = {
        ...pagination,
        currentPage: currentPage ? currentPage : pagination.currentPage,
        // NMU-580: the order travels with the page — see created-sort.js.
        ...activeSort,
      };
      dispatch(appLoaderAction.enable());

      const activeCondition = overrides ? overrides.condition : condition;

      // NMU-579: keywordList is no longer a search condition. The backend skips
      // the criterion when it is absent, so nothing else changes.
      const newCondition = {
        title: activeCondition.title,
        status: activeCondition.status,
        createBy: activeCondition.createBy,
        /*
         * NMU-450: no layer here — the table below the tabs is one common pool.
         * It used to be filtered to the active tab, so Layer B and C opened
         * empty and an editor had to retype links that already existed under
         * Layer A. Placement is what the Layer tabs decide, not ownership.
         */
      };
      const result = await footerLinkService.search(newCondition, newPaginate);
      setDataList(result.list);
      setPagination(result.pagination ? result.pagination : newPaginate);
      setSelectedRowKeys([]);
      setSelectedRows([]);

      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      //dispatch(notifyUtitls.error(err));
      notifyUtitls.error(err)
    }
  };

  /**
   * NMU-529: clearing the filters has to re-run the search, not just empty the
   * inputs — otherwise the table keeps showing the previous result set while the
   * form claims no filter is applied. Back to page 1, since the record the user
   * was looking at on page 3 of a filtered list is somewhere else entirely in
   * the full one.
   */
  const onClearSearch = () => {
    const cleared = { title: null, status: null, createBy: null };
    setCondition(cleared);
    doSearch(1, { condition: cleared });
  };

  const onClickEdit = (record) => {
    try {
      dispatch(appLoaderAction.enable());
      setEditData(record);
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const onConfirmDelete = (id) => {
    onDelete(id);
  };

  const onDelete = async (id) => {
    try {
      dispatch(appLoaderAction.enable());
      await footerLinkService.remove({ deleteId: id });
      dispatch(appLoaderAction.disale());
      notifyUtitls.deleted("สำเร็จ", "ลบข้อมูลสำเร็จ");
      doSearch(1);
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const rowSelection = () => {
    return {
      onChange: (selectedRowKeys, selectedRows) => {
        setSelectedRows(selectedRows);
        setSelectedRowKeys(selectedRowKeys);
      },
      selectedRowKeys,
    };
  };

  // NMU-565: the single-layer reload went with the single-layer save — the only
  // caller left (ล้างค่า) has to reset every layer, not just the open tab.
  const doSearchAllDisplays = async () => {
    try {
      dispatch(appLoaderAction.enable());

      const results = await Promise.all(
        FOOTER_LAYERS.map((layer) => footerLinkService.getDisplay(layer))
      );

      const next = {};
      FOOTER_LAYERS.forEach((layer, i) => {
        next[layer] = results[i]?.list || [];
      });
      setDisplayByLayer(next);

      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const onAddToDisplay = () => {
    if (selectedRows.length === 0) {
      notifyUtitls.warning(
        "เพิ่มการแสดงลิงค์ส่วนท้าย",
        "กรุณาเลือกลิงค์ส่วนท้ายอย่างน้อย 1 รายการ"
      );
    } else if (displayList.length + selectedRows.length > MAX_LINKS_PER_LAYER) {
      notifyUtitls.warning(
        "เพิ่มการแสดงลิงค์ส่วนท้าย",
        `Layer ${activeLayer} แสดงได้สูงสุด ${MAX_LINKS_PER_LAYER} ลิงค์`
      );
    } else {
      const compare = selectedRows.filter((item) => {
        return (
          displayList.findIndex(
            (serviceLink) => serviceLink.footerLinkId === item.footerLinkId
          ) < 0
        );
      });
      const newDisplayList = [...displayList, ...compare];

      setDisplayForLayer(activeLayer, newDisplayList);
      setSelectedRows([]);
      setSelectedRowKeys([]);
    }
  };

  /**
   * NMU-565: one save for the whole footer.
   *
   * The button used to submit only the open tab, so arranging all three layers
   * meant three visits and three saves — and any layer left unsaved silently
   * kept its old arrangement. Every layer now goes in a single request
   * (`listBeanByLayer`), which is also what makes the cache eviction on the
   * service side cover the footer once rather than once per layer.
   */
  const onSaveDisplay = async () => {
    try {
      dispatch(appLoaderAction.enable());

      const listBeanByLayer = {};
      FOOTER_LAYERS.forEach((layer) => {
        listBeanByLayer[layer] = displayByLayer[layer] || [];
      });

      await footerLinkService.saveDisplay({ listBeanByLayer });

      dispatch(appLoaderAction.disale());
      notifyUtitls.success("สำเร็จ", "บันทึกการแสดงผลลิงก์ส่วนท้ายเรียบร้อยแล้ว");
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  // NMU-565: clearing has to match the save's scope. With a global save, resetting
  // only the open tab would leave the other layers' unsaved edits to be written by
  // the next save — a change the user thought they had discarded.
  const onClearDisplay = () => {
    doSearchAllDisplays();
  };

  const doLoadFooterSettings = async () => {
    try {
      const result = await footerSettingsService.get();
      setFooterSettings({ ...emptyFooterSettings, ...result });
    } catch (err) {
      notifyUtitls.error(err);
    }
  };

  const onChangeFooterSettings = (event) => {
    const { name, value } = event.target;
    setFooterSettings({ ...footerSettings, [name]: value });
  };

  const onSaveFooterSettings = async () => {
    try {
      dispatch(appLoaderAction.enable());
      const saved = await footerSettingsService.save(footerSettings);
      setFooterSettings({ ...emptyFooterSettings, ...saved });
      dispatch(appLoaderAction.disale());
      notifyUtitls.success("สำเร็จ", "บันทึกข้อมูลติดต่อสำเร็จ");
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const onRemoveDisplay = (serviceLinkId) => {
    const newList = [...displayList];
    const index = displayList.findIndex(
      (item) => item.serviceLinkId === serviceLinkId
    );
    newList.splice(index, 1);
    setDisplayForLayer(activeLayer, newList);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setDisplayForLayer(activeLayer, arrayMove(displayList, oldIndex, newIndex));
  };

  const onPreview = async () => {
    try {
      dispatch(appLoaderAction.enable());

      // dispatch(serviceLinkPreviewAction.addServiceLinkPreview(displayList));

      await openWebsitePreview({ type: PREVIEW_TYPE.footerLink, listBean: displayList });
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const renderOptionUser = () => {
    return userList?.map((item) => {
      if (item?.profile) {
        const profile = item.profile;
        return (
          <Select.Option value={item.userId} key={item.userId}>
            {`${profile.firstName} ${profile.lastName}`}
          </Select.Option>
        );
      } else {
        return null;
      }
    });
  };

  const onChangeSelect = (name) => {
    return (value) => {
      setCondition({ ...condition, [name]: value });
    };
  };

  return (
    <div>
      <h2 className="color-primary-title">ลิงค์ส่วนท้าย</h2>
      <Breadcrumb>
        <Breadcrumb.Item>การจัดการเนื้อหา</Breadcrumb.Item>
        <Breadcrumb.Item>ลิงค์ส่วนท้าย</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />
      <div className="d-flex justify-content-between">
        <Button type="primary" onClick={() => onAdd()}>
          <Icon.Plus className="me-1" size={18} /> เพิ่มลิงค์ส่วนท้าย
        </Button>
        <div>
          <Button className="btn-gray" onClick={() => onClearDisplay()}>
            ล้างค่าการแสดงผลลิงค์ส่วนท้าย
          </Button>
          {/* NMU-565: the label carries no layer name — this saves all of them. */}
          <Button
            id="save-footer-display"
            type="primary"
            className="mx-2"
            onClick={() => onSaveDisplay()}
          >
            <Icon.Save className="me-1" size={18} /> บันทึกการแสดงผลลิงก์ส่วนท้าย
          </Button>
          <Button className="mx-2 btn-line" onClick={() => onPreview()}>
            <Icon.Eye className="me-1" size={18} /> ดูตัวอย่างเว็บไซต์
          </Button>
        </div>
      </div>
      <Card className="mt-4" bordered={false}>
        <Collapse className="mb-4" defaultActiveKey={["1"]} onChange={callback}>
          <Panel header="ตัวกรอง" key="1">
            <div>
              <Form layout="vertical">
                <Row gutter={20}>
                  <Col span={8}>
                    <Form.Item label="ชื่อลิงค์ส่วนท้าย" className="mb-0">
                      <Input
                        name="title"
                        value={condition.title}
                        onChange={onChangeCondition}
                      />
                    </Form.Item>
                  </Col>
                  {/* NMU-579: no คีย์เวิร์ด filter here. Keywords are something
                      an editor sets on a link in the add/edit dialog — they were
                      never a useful way to find one. The dialog keeps its
                      keyword input. */}
                  <Col span={8}>
                    <Form.Item label="ผู้บันทึก" className="mb-0">
                      <Select
                        showSearch
                        placeholder="Search to Select"
                        optionFilterProp="children"
                        allowClear
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA, optionB) =>
                          optionA.children
                            .toLowerCase()
                            .localeCompare(optionB.children.toLowerCase())
                        }
                        value={condition.createBy}
                        onChange={onChangeSelect("createBy")}
                      >
                        {renderOptionUser()}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="สถานะ" className="mb-0">
                      <Select
                        name="status"
                        value={condition.status}
                        onChange={onChangeSelect("status")}
                      >
                        <Option value={null}>All</Option>
                        <Option value={true}>Online</Option>
                        <Option value={false}>Offline</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={20}>
                  <Col className="mt-4">
                    <Button
                      type="primary"
                      className="w-100"
                      onClick={() => doSearch(1)}
                    >
                      <Icon.Search className="me-1" size={18} /> ค้นหา
                    </Button>
                  </Col>
                  <Col className="mt-4">
                    <Button
                      className="btn-gray"
                      onClick={() => onClearSearch()}
                    >
                      ล้างค่า
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </Panel>
        </Collapse>
        {/* NMU-421: one tab per footer layer, all three identical — card grid
            preview, add button and data table — each with its own arrangement
            and its own cap. */}
        <Tabs
          id="footer-layer-tabs"
          className="mb-4"
          activeKey={activeLayer}
          onChange={(key) => setActiveLayer(key)}
        >
          {FOOTER_LAYERS.map((layer) => (
            <TabPane tab={`Layer ${layer}`} key={layer}>
              <div className="mb-2 color-primary">
                {`การแสดงผลลิงค์ส่วนท้าย Layer ${layer} (ได้สูงสุด ${MAX_LINKS_PER_LAYER} ลิงค์ส่วนท้าย) — ใช้อยู่ ${displayList.length}/${MAX_LINKS_PER_LAYER}`}
              </div>

              <div className="boder-main p-3 mb-3">
                {displayList.length > 0 ? (
                  <SortableList
                    axis={"xy"}
                    items={displayList}
                    onSortEnd={onSortEnd}
                    onRemove={onRemoveDisplay}
                    groupValue={groupType.FOOTER_LINK}
                  />
                ) : (
                  <Empty />
                )}
              </div>

              <div className="mb-3">
                <Button
                  id={`add-to-display-${layer}`}
                  type="primary"
                  disabled={isLayerFull}
                  onClick={() => onAddToDisplay()}
                >
                  <Icon.Plus className="me-1" size={18} />{" "}
                  เพิ่มการแสดงผลลิงค์ส่วนท้าย
                </Button>
                {isLayerFull ? (
                  <span className="ms-2 text-muted">
                    {`Layer ${layer} ครบ ${MAX_LINKS_PER_LAYER} ลิงค์แล้ว — นำรายการออกก่อนจึงจะเพิ่มได้`}
                  </span>
                ) : null}
              </div>

              <CustomTable
                rowSelection={{
                  type: "checkbox",
                  ...rowSelection(),
                }}
                rowKey="footerLinkId"
                tableName={`footerLinkTable${layer}`}
                columns={withCreatedColumn(columns, createdColumn)}
                onChangeTable={onChangeTable}
                data={dataList}
                scroll={{ x: true }}
                isPagination={true}
                onChangePage={onChangePage}
                currentPage={pagination.currentPage}
                pageSize={pagination.rowPerPage}
                totalRecord={pagination.rowCount}
              />
            </TabPane>
          ))}
        </Tabs>

        {/* NMU-421: the collapsible "Layer C" accordion is gone — Layer C is a
            link layer now. These contact fields are still the only way to edit
            what sits at the very bottom of the public footer, so they stay as a
            plain section under the tabs rather than being deleted with it. */}
        <div id="footer-contact-section" className="mt-4 mb-4">
          <h5 className="color-primary mb-3">ข้อมูลติดต่อและลิงก์ด่วน (ท้ายเว็บไซต์)</h5>
            <Form layout="vertical">
              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <Form.Item label="ที่อยู่ (ไทย)" className="mb-3">
                    <Input.TextArea
                      name="addressTH"
                      rows={2}
                      value={footerSettings.addressTH}
                      onChange={onChangeFooterSettings}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Address (English)" className="mb-3">
                    <Input.TextArea
                      name="addressEN"
                      rows={2}
                      value={footerSettings.addressEN}
                      onChange={onChangeFooterSettings}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="เบอร์โทรศัพท์" className="mb-3">
                    <Input
                      name="phone"
                      value={footerSettings.phone}
                      onChange={onChangeFooterSettings}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="อีเมล" className="mb-3">
                    <Input
                      name="email"
                      value={footerSettings.email}
                      onChange={onChangeFooterSettings}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="ลิงก์แผนผังและการเดินทาง" className="mb-3">
                    <Input
                      name="mapsUrl"
                      value={footerSettings.mapsUrl}
                      onChange={onChangeFooterSettings}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="ลิงก์ส่วนงานบริหารมหาวิทยาลัย" className="mb-3">
                    <Input
                      name="universityOfficesUrl"
                      value={footerSettings.universityOfficesUrl}
                      onChange={onChangeFooterSettings}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Facebook URL" className="mb-3">
                    <Input
                      name="facebookUrl"
                      value={footerSettings.facebookUrl}
                      onChange={onChangeFooterSettings}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Twitter URL" className="mb-3">
                    <Input
                      name="twitterUrl"
                      value={footerSettings.twitterUrl}
                      onChange={onChangeFooterSettings}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Button type="primary" onClick={() => onSaveFooterSettings()}>
                <Icon.Save className="me-1" size={18} /> บันทึกข้อมูลติดต่อ
              </Button>
            </Form>
        </div>

      </Card>
      <AddFooterLinkDialog
        isShow={isShowAddFileDialog}
        editData={editData}
        onCancel={onCancel}
        onSaveComplete={onSaveComplete}
      ></AddFooterLinkDialog>
    </div>
  );
};

export default FooterLink;
